import { TrendingUp, TrendingDown } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { useCategoryDetail } from './context'
import { formatAmount } from '@/lib/utils'
import type { BudgetItem } from '@/lib/hooks'

type Props = {
  budget?: BudgetItem
  previousMonthAmount?: number | null
}

export function CategorySummarySection({ budget, previousMonthAmount }: Props) {
  const { categoryAmount, monthlyTotal } = useCategoryDetail()

  const totalPercentage = monthlyTotal > 0 ? (categoryAmount / monthlyTotal) * 100 : null
  const budgetPercentage = budget ? Math.round((categoryAmount / budget.budget_amount) * 100) : null

  const comparison =
    previousMonthAmount != null
      ? {
          prevAmount: previousMonthAmount,
          delta: categoryAmount - previousMonthAmount,
          percentage:
            previousMonthAmount > 0
              ? Math.round(((categoryAmount - previousMonthAmount) / previousMonthAmount) * 100)
              : null,
        }
      : null

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums">{formatAmount(categoryAmount)}</span>
        {totalPercentage !== null && (
          <span className="text-sm text-muted-foreground tabular-nums">
            全体の{totalPercentage.toFixed(1)}%
          </span>
        )}
      </div>

      {budget && budgetPercentage !== null && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>予算 {formatAmount(budget.budget_amount)}</span>
            <span>{budgetPercentage}%</span>
          </div>
          <Progress value={Math.min(budgetPercentage, 100)} />
          {budget.remaining < 0 && (
            <div className="text-xs text-destructive">
              {formatAmount(Math.abs(budget.remaining))} オーバー
            </div>
          )}
        </div>
      )}

      {comparison && comparison.prevAmount > 0 && (
        <div className="flex items-center gap-1 text-sm">
          <span className="text-muted-foreground">前月比</span>
          {comparison.delta >= 0 ? (
            <span className="flex items-center gap-0.5 text-destructive">
              <TrendingUp className="h-3.5 w-3.5" />
              +{formatAmount(comparison.delta)}
              {comparison.percentage !== null && (
                <span className="text-xs">({comparison.percentage}%)</span>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-0.5 text-green-600">
              <TrendingDown className="h-3.5 w-3.5" />
              {formatAmount(comparison.delta)}
              {comparison.percentage !== null && (
                <span className="text-xs">({comparison.percentage}%)</span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
